import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  Button,
  Grid,
  Stack,
  Step,
  StepButton,
  Stepper
} from '@mui/material'
import { isEmpty } from 'lodash'
import React, { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { useToggle } from 'react-use'

import agent from 'agent'
import { ProvenanceTargetType } from 'components/attacks/create/create-attack-types'
import Content from 'components/common/Content'
import { ReliabilityManagement101 } from 'components/getting-started/ReliabilityManagement101'
import Icon from 'components/Icon'
import { EditGoldenSignalProvider } from 'components/services/create/EditGoldenSignalContext'
import { ServiceAutoscheduling } from 'components/services/create/ServiceAutoscheduling'
import { ServiceFingerprint } from 'components/services/create/ServiceFingerprint'
import { ServiceGoldenSignal } from 'components/services/create/ServiceGoldenSignal'
import { ServiceProcessDependenciesSelection } from 'components/services/create/ServiceProcessDependenciesSelection'
import { ServiceSummary } from 'components/services/create/ServiceSummary'
import { ServiceType } from 'components/services/create/ServiceType'
import {
  getInitialScheduleFormSettings,
  getScheduleSettings,
  isAutoSchedulingValid,
  serviceFormSchemaValidation
} from 'components/services/service-utils'
import useAuth from 'hooks/use-auth'
import { useGremlinQuery } from 'hooks/use-gremlin-query'
import { useProcessView } from 'hooks/use-process-view'
import toast from 'lib/toast'
import theme from 'theme'
import { Client } from 'types/api/attacks/client'
import {
  ContainerTargetingStrategy,
  HostTargetingStrategy,
  KubernetesTargetingStrategy,
  TargetingStrategy
} from 'types/api/attacks/details/targeting'
import { ServiceCreationReadiness } from 'types/api/services/service-creation-readiness'
import { ServiceFormValues } from 'types/forms'
import { messageFromResponseError } from 'utils/strings'

export enum ViewName {
  ServiceType = 'Service Type',
  ServiceFingerprint = 'Service Fingerprint',
  Process = 'Process',
  GoldenSignals = 'Golden Signals',
  AutoScheduling = 'Autoscheduling',
  Summary = 'Summary'
}

export interface ViewItem {
  name: ViewName
  order: number
  primaryButtonText: string
  secondaryButtonText: string | null
}
export const views: ViewItem[] = [
  {
    name: ViewName.ServiceType,
    order: 0,
    primaryButtonText: 'Next',
    secondaryButtonText: null
  },
  {
    name: ViewName.ServiceFingerprint,
    order: 1,
    primaryButtonText: 'Next',
    secondaryButtonText: 'Back'
  },
  {
    name: ViewName.Process,
    order: 2,
    primaryButtonText: 'Next',
    secondaryButtonText: 'Back'
  },
  {
    name: ViewName.GoldenSignals,
    order: 3,
    primaryButtonText: 'Next',
    secondaryButtonText: 'Back'
  },
  {
    name: ViewName.AutoScheduling,
    order: 4,
    primaryButtonText: 'Next',
    secondaryButtonText: 'Back'
  },
  {
    name: ViewName.Summary,
    order: 5,
    primaryButtonText: 'Create Service',
    secondaryButtonText: 'Back'
  }
]

const steps = views.map((view) => view.name)

export const formatOwnerDetails = (identifier, firstName, lastName) => {
  return firstName && lastName
    ? `${firstName} ${lastName} (${identifier})`
    : firstName
    ? `${firstName} (${identifier})`
    : lastName
    ? `${lastName} (${identifier})`
    : identifier
}

export const CreateServiceStepper: React.FC<{
  initialActiveView?: ViewItem
}> = ({ initialActiveView }) => {
  const { currentTeamId, session } = useAuth()

  const { identifier, first_name, last_name } = session

  const history = useHistory()

  const [activeView, setActiveView] = useState<ViewItem>(
    initialActiveView ?? views[0]
  )
  const [processViewError, setProcessViewError] = useState('')
  const [processViewEndpoint, setProcessViewEndpoint] = useState('')
  const [processViewLoading, setProcessViewLoading] = useToggle(false)
  const [disableFutureSteps, setDisableFutureSteps] = useToggle(false)

  // Keeps track of completed steps
  const [isStepCompleted, setIsStepCompleted] = useState(
    steps.reduce((stepsCompleted, step) => {
      stepsCompleted[step] = false
      return stepsCompleted
    }, {})
  )

  const isLastView = activeView.order === views.length - 1

  const defaultValues: ServiceFormValues = {
    name: '',
    processId: '',
    processName: '',
    scheduleSettings: getInitialScheduleFormSettings(),
    selectedLabels: '',
    selectedNamespace: '',
    selectedObjects: new Set(),
    selectedClients: [] as Client[],
    statusCheckIds: [] as string[],
    statusCheckNames: [] as string[],
    targetAll: false,
    targetParams: {},
    targetingStrategy: { type: ProvenanceTargetType.ProcessData },
    type: ProvenanceTargetType.ProcessData
  }

  const form = useForm<ServiceFormValues>({
    mode: 'onChange', // validates form state on input change
    defaultValues,
    resolver: yupResolver(serviceFormSchemaValidation)
  })

  const { formState, getValues, handleSubmit, setValue, watch } = form
  const { isSubmitting, isValid } = formState

  // This watches the "targetingStrategy" form value and initiates a re-render on change.  This is designed
  // to facilitate the function of the "handleGetProcessesView" callback
  const targetingStrategy = watch('targetingStrategy')
  const targetType = watch('type')

  const { data: readinessData, isError: isReadinessDataError } =
    useGremlinQuery<ServiceCreationReadiness>(
      `services/service-creation-readiness/${targetType}`
    )

  /**
   * We still allow service creation process to continue while an error occurred on retrieving readiness data
   */
  const isServiceCreationReady =
    isReadinessDataError || readinessData?.hasProcesses

  const handleBack = () => setActiveView(views[activeView.order - 1])

  const handleNext = async () => {
    if (!stepValidation[activeView.name]) {
      setIsStepCompleted((prevState) => {
        prevState[activeView.name] = true
        return prevState
      })
    }

    switch (activeView.name) {
      case ViewName.ServiceFingerprint:
        setValue('processId', '')

        if (await handleGetProcessesView()) {
          setActiveView(views[activeView.order + 1])
        }
        setDisableFutureSteps(false)
        break
      case ViewName.Summary:
        break
      default:
        !isLastView && setActiveView(views[activeView.order + 1])
        break
    }
  }

  const handleSummaryEdit = (step) => setActiveView(views[step])

  const { refetch: refetchProcesses, status: processRefetchStatus } =
    useProcessView({
      enabled: Boolean(processViewEndpoint),
      endpoint: processViewEndpoint
    })

  const handleGetProcessesView = async (): Promise<boolean> => {
    setProcessViewError('')

    try {
      setProcessViewLoading(true)
      await agent
        .post(`/processes/views/?teamId=${currentTeamId}`, {
          targetingStrategy
        })
        .then((res) => setProcessViewEndpoint(res.headers?.location))
      return true
    } catch (e) {
      const error = messageFromResponseError(
        e,
        'There was an issue getting a list of your processes. Please click back and try again later.'
      )
      setProcessViewError(error)
      toast({ type: 'error', text: error })
      return false
    } finally {
      setProcessViewLoading(false)
    }
  }

  const onSubmit = async (values) => {
    try {
      const saveRes = await agent.post(`/services?teamId=${currentTeamId}`, {
        name: values.name,
        processId: values.processId,
        scheduleSettings: getScheduleSettings(values.scheduleSettings),
        statusCheckIds: values.statusCheckIds,
        targetingStrategy: values.targetingStrategy,
        serviceOwner: {
          identifier: identifier,
          firstName: first_name,
          lastName: last_name
        }
      })

      setProcessViewError('')
      toast({ type: 'success', text: 'Service Created' })

      const guid = saveRes?.data
      history.push(`/services/${guid}`)
    } catch (e) {
      const error = messageFromResponseError(
        e,
        'There was an issue creating this service. Please try again later.'
      )
      toast({ type: 'error', text: error })

      return
    }
  }

  const isTargetingStrategyEmpty = (targetingStrategy: TargetingStrategy) => {
    switch (targetingStrategy.type) {
      case ProvenanceTargetType.ProcessData:
        return isEmpty(
          (targetingStrategy as HostTargetingStrategy).multiSelectTags
        )
      case ProvenanceTargetType.Container:
        return isEmpty(
          (targetingStrategy as ContainerTargetingStrategy).multiSelectLabels
        )
      case ProvenanceTargetType.Kubernetes:
        return isEmpty((targetingStrategy as KubernetesTargetingStrategy).names)
      default:
        return true
    }
  }

  /**
   * Validation object returns true for invalid keys.
   *
   * The 'watch' react-hook-form method is used here because each step of the Create Service workflow is manually
   * validated after each change in value. The 'getValues' react-hook-form method is entirely passive and is only
   * updated when the component where it is called goes through a re-render. The 'watch' method triggers the component
   * where the react-hook-form form object is instantiated to re-render.
   *
   * This implementation is not ideal. The Create Service form needs to be overhauled to implement a validation schema
   * that can be used via the 'trigger' react-hook-form method to validate each step.
   *
   * TODO: Refactor the validation of the Create Service form to use a yup schema for each step
   */
  const stepValidation = {
    [ViewName.ServiceType]:
      Boolean(formState.errors.name) ||
      !getValues('name').trim() ||
      !isServiceCreationReady,
    [ViewName.ServiceFingerprint]: isTargetingStrategyEmpty(targetingStrategy),
    [ViewName.Process]: !watch('processId'),
    [ViewName.GoldenSignals]: isEmpty(getValues('statusCheckIds')),
    [ViewName.AutoScheduling]: isAutoSchedulingValid(watch('scheduleSettings'))
  }

  const hasStepErrors = () => {
    switch (activeView.name) {
      case ViewName.ServiceType:
        return stepValidation[ViewName.ServiceType]
      case ViewName.ServiceFingerprint:
        return stepValidation[ViewName.ServiceFingerprint]
      case ViewName.Process:
        return stepValidation[ViewName.Process]
      case ViewName.GoldenSignals:
        return stepValidation[ViewName.GoldenSignals]
      case ViewName.AutoScheduling:
        return stepValidation[ViewName.AutoScheduling]
      default:
        return false
    }
  }

  const handleStepClick = (step) => () => {
    // Check to see if the current view is still valid and set completed to false if it is not
    if (stepValidation[activeView.name]) {
      setIsStepCompleted((prevState) => {
        prevState[activeView.name] = false
        return prevState
      })
    }

    if (step < activeView.order) {
      setActiveView(views[step])
    } else if (!hasStepErrors()) {
      setActiveView(views[step])
    }
  }

  const handleGoldenSignalChange = (statusChecks) => {
    setValue(
      'statusCheckIds',
      statusChecks.map((s) => s.id)
    )
    setValue(
      'statusCheckNames',
      statusChecks.map((s) => s.name)
    )
  }

  const isStepDisabled = (step) => {
    if (step === activeView.name) return true

    // check if the previous steps are all validated
    switch (step) {
      case ViewName.ServiceType:
        return false
      case ViewName.ServiceFingerprint:
        return stepValidation[ViewName.ServiceType] || disableFutureSteps
      case ViewName.Process:
        return stepValidation[ViewName.ServiceFingerprint] || disableFutureSteps
      case ViewName.GoldenSignals:
        return stepValidation[ViewName.Process] || disableFutureSteps
      case ViewName.AutoScheduling:
        return stepValidation[ViewName.GoldenSignals] || disableFutureSteps
      case ViewName.Summary:
        return (
          stepValidation[ViewName.AutoScheduling] ||
          !isStepCompleted[ViewName.AutoScheduling] || // Since the Autoscheduling values are valid at initialization
          disableFutureSteps
        )
      default:
        return false
    }
  }

  return (
    <Content>
      <Stepper
        data-test-create-service-stepper
        nonLinear
        activeStep={activeView.order}
        css={{
          paddingBottom: theme.space[5]
        }}
      >
        {steps.map((step, index) => (
          <Step key={step} completed={isStepCompleted[step]}>
            <StepButton
              color="inherit"
              data-test-create-service-selected-step={activeView.name === step}
              data-test-service-new-stepper={step}
              disabled={isStepDisabled(step)}
              onClick={handleStepClick(index)}
            >
              {step}
            </StepButton>
          </Step>
        ))}
      </Stepper>
      <FormProvider {...form}>
        <form css={{ display: 'grid' }} onSubmit={handleSubmit(onSubmit)}>
          <Grid container direction="row" spacing={2}>
            <Grid item lg={9} xs={12}>
              <Stack direction="column" gap={4}>
                {activeView.name === ViewName.ServiceType && (
                  /**
                   * Since the validation for moving through this form's stepper is implemented manually, this state
                   * change function must be passed down as a prop to each component that affects whether future steps
                   * are to be disabled.
                   *
                   * TODO: Refactor the validation of the Create Service form to use a yup schema for each step
                   */
                  <ServiceType
                    serviceCreationReadiness={readinessData}
                    setDisableFutureSteps={setDisableFutureSteps}
                  />
                )}
                {activeView.name === ViewName.ServiceFingerprint && (
                  /**
                   * Since the validation for moving through this form's stepper is implemented manually, this state
                   * change function must be passed down as a prop to each component that affects whether future steps
                   * are to be disabled.
                   *
                   * TODO: Refactor the validation of the Create Service form to use a yup schema for each step
                   */
                  <ServiceFingerprint
                    setDisableFutureSteps={setDisableFutureSteps}
                  />
                )}
                {activeView.name === ViewName.Process && (
                  <ServiceProcessDependenciesSelection
                    handleGetProcessesView={handleGetProcessesView}
                    processViewError={processViewError}
                    refetchStatus={processRefetchStatus}
                    onRetry={refetchProcesses}
                  />
                )}
                {activeView.name === ViewName.GoldenSignals && (
                  <EditGoldenSignalProvider
                    externalIntegrationType="GOLDEN_SIGNAL"
                    statusCheckIds={watch('statusCheckIds')}
                  >
                    <ServiceGoldenSignal onChanged={handleGoldenSignalChange} />
                  </EditGoldenSignalProvider>
                )}
                {activeView.name === ViewName.AutoScheduling && (
                  <ServiceAutoscheduling />
                )}
                {activeView.name === ViewName.Summary && (
                  <ServiceSummary steps={steps} onEdit={handleSummaryEdit} />
                )}
                <Box>
                  {activeView.secondaryButtonText && (
                    <Button
                      data-test-service-new-back
                      css={{ marginRight: theme.space[3] }}
                      startIcon={<Icon icon="arrowLeft" />}
                      variant="contained"
                      onClick={handleBack}
                    >
                      {activeView.secondaryButtonText}
                    </Button>
                  )}
                  {activeView.primaryButtonText && !isLastView ? (
                    <Button
                      key="nextStep"
                      css={{ marginRight: theme.space[3] }}
                      data-test-service-new-next={activeView.name}
                      disabled={
                        hasStepErrors() || !isValid || processViewLoading
                      }
                      endIcon={<Icon icon="arrowRight" />}
                      variant="contained"
                      onClick={handleNext}
                    >
                      {activeView.primaryButtonText}
                    </Button>
                  ) : (
                    <Button
                      key="formSubmit"
                      data-test-service-new-submit
                      css={{ marginRight: theme.space[3] }}
                      disabled={
                        hasStepErrors() ||
                        !isValid ||
                        isSubmitting ||
                        processViewLoading
                      }
                      type="submit"
                      variant="contained"
                    >
                      {activeView.primaryButtonText}
                    </Button>
                  )}
                </Box>
              </Stack>
            </Grid>
            {activeView.name !== ViewName.Summary && (
              <Grid item lg={3} xs={12}>
                <ReliabilityManagement101 />
              </Grid>
            )}
          </Grid>
        </form>
      </FormProvider>
    </Content>
  )
}
